import { useState } from 'react';
import { apiClient } from '../../auth-lib';
import { Button, Card, useToast } from '../../design-system';

// "Task History" surfaces the task's comment thread (kept reachable from the
// redesigned page). The organisation-wide audit log is super-admin-only, so
// it isn't shown here.
const STATUS_LABEL = {
  todo: 'Created', 'in-progress': 'In Progress', review: 'Review',
  'waiting-client': 'Waiting on Client', completed: 'Completed', 'on-hold': 'On Hold',
};
const statusText = (s) => STATUS_LABEL[s] || s || '—';

const ACTION_META = {
  completed: { icon: '✅', label: 'Completed' },
  reopened: { icon: '🔓', label: 'Reopened' },
  status_changed: { icon: '🔄', label: 'Status changed' },
};

function TaskHistoryTab({ task, setTask }) {
  const toast = useToast();
  const [comment, setComment] = useState('');
  const [saving, setSaving] = useState(false);
  const comments = task.comments || [];
  const statusHistory = task.statusHistory || [];

  const submit = async (e) => {
    e.preventDefault();
    if (!comment.trim()) return;
    setSaving(true);
    try {
      const res = await apiClient.post(`/api/tasks/${task._id}/comments`, { text: comment });
      // The comments endpoint returns an unpopulated task — merge only the
      // comments so populated refs on the current task copy survive.
      setTask((prev) => ({ ...prev, comments: res.data.comments }));
      setComment('');
    } catch (err) {
      toast.danger('Failed to add comment');
    } finally {
      setSaving(false);
    }
  };

  return (
    <Card padding="md">
      <div className="td-card-head"><span className="td-card-title">Task History</span></div>

      {/* Status timeline — every completion and reopen, newest first. */}
      <div className="td-status-history">
        <div className="td-history-subhead">Status timeline</div>
        {statusHistory.length === 0 ? (
          <div className="td-muted">No status changes yet.</div>
        ) : (
          [...statusHistory].reverse().map((h, i) => {
            const meta = ACTION_META[h.action] || ACTION_META.status_changed;
            return (
              <div key={i} className="td-status-event">
                <span className="td-status-event-icon" aria-hidden="true">{meta.icon}</span>
                <div className="td-status-event-body">
                  <div className="td-status-event-line">
                    <strong>{meta.label}</strong>
                    {' — '}
                    {statusText(h.from)} → {statusText(h.to)}
                  </div>
                  <div className="td-muted td-status-event-meta">
                    {h.byName || 'Someone'} · {h.at ? new Date(h.at).toLocaleString() : ''}
                  </div>
                </div>
              </div>
            );
          })
        )}
      </div>

      <div className="td-history-note td-muted">Comments on this task.</div>

      <div className="td-comments">
        {comments.length === 0 ? (
          <div className="td-muted">No activity yet.</div>
        ) : (
          [...comments].reverse().map((c, i) => (
            <div key={i} className="td-comment">
              <div className="td-comment-head">
                <strong>{c.author}</strong>
                <span className="td-muted">{new Date(c.createdAt).toLocaleString()}</span>
              </div>
              <p className="td-comment-text">{c.text}</p>
            </div>
          ))
        )}
      </div>

      <form onSubmit={submit} className="td-comment-form">
        <textarea
          className="td-inline-input td-comment-input"
          placeholder="Add a comment..."
          rows="3"
          value={comment}
          onChange={(e) => setComment(e.target.value)}
        />
        <Button type="submit" variant="secondary" size="sm" loading={saving}>Post Comment</Button>
      </form>
    </Card>
  );
}

export default TaskHistoryTab;
